import type { CitationAttrs } from '#src/types/markdown.ts';
import { citationHash } from '#src/contexts/citation-registry.context.svelte.ts';

// Matches <citation ...>text</citation> – non-greedy inner content
const PAIRED_TAG_RX = () =>
	/<citation((?:\s+[\w_]+=(?:"[^"]*"|'[^']*'))+)\s*>([\s\S]*?)<\/citation\s*>/gi;
// Matches <citation ... />
const SELF_CLOSING_TAG_RX = () => /<citation((?:\s+[\w_]+=(?:"[^"]*"|'[^']*'))+)\s*\/>/gi;

function parseAttrs(attrStr: string): CitationAttrs {
	const attrs: CitationAttrs = {};
	const chunkMatch = attrStr.match(/\bchunk_id="(\d+)"/i);
	if (chunkMatch) {
		attrs.chunkId = parseInt(chunkMatch[1], 10);
	}
	const docMatch = attrStr.match(/\bdoc_id="(\d+)"/i);
	if (docMatch) {
		attrs.docId = parseInt(docMatch[1], 10);
	}
	const webMatch = attrStr.match(/\bweb_id="([^"]+)"/i);
	if (webMatch) {
		attrs.webId = webMatch[1];
	}
	return attrs;
}

/** Extract all CitationAttrs from a raw LLM message string. */
export function parseCitationsFromText(text: string): CitationAttrs[] {
	const paired = [...text.matchAll(PAIRED_TAG_RX())].map(([, rawAttrs, rawQuote]) => {
		const attrs = parseAttrs(rawAttrs);
		const quote = rawQuote.trim();
		return { ...attrs, quote };
	});

	const selfClosing = [...text.matchAll(SELF_CLOSING_TAG_RX())].map(([, rawAttrs]) =>
		parseAttrs(rawAttrs)
	);

	return [...paired, ...selfClosing];
}

/** Rewrite all <citation> tags in text to include a hash="..." attribute (idempotent). */
export function injectHashesIntoText(text: string): string {
	if (!text) {
		return text;
	}

	return text
		.replace(PAIRED_TAG_RX(), (_, attrStr: string, quote: string) => {
			if (/\bhash="/.test(attrStr)) {
				return _;
			}
			const attrs = parseAttrs(attrStr);
			const trimmedQuote = quote.trim() || undefined;
			if (trimmedQuote) {
				attrs.quote = trimmedQuote;
			}
			const hash = citationHash(attrs);
			return `<citation${attrStr} hash="${hash}">${quote}</citation>`;
		})
		.replace(SELF_CLOSING_TAG_RX(), (_, attrStr: string) => {
			if (/\bhash="/.test(attrStr)) {
				return _;
			}
			const attrs = parseAttrs(attrStr);
			const hash = citationHash(attrs);
			return `<citation${attrStr} hash="${hash}" />`;
		});
}
